import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { HERO } from "@/constants/testIds";

const HERO_IMG = "/waylio-hero.jpg";
const ROUTE_ILLUSTRATION = "/waylio-route-illustration.jpg";

export default function Hero() {
  return (
    <section
      data-testid={HERO.root}
      className="relative pt-10 md:pt-12 pb-16 md:pb-20 overflow-hidden bg-paper"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10 relative">
        {/* Left: Headline */}
        <div className="col-span-12 lg:col-span-7 flex flex-col justify-center">
          <h1
            data-testid={HERO.headline}
            className="font-serif text-[10vw] md:text-[6vw] lg:text-[5.2vw] xl:text-[5rem] font-bold leading-[1.25] text-forest tracking-[-0.02em]"
          >
            <span className="block">
              <span>The Right </span>
              <span className="italic font-medium text-terracotta">Way,</span>
            </span>
            <span className="block">
              <span>Right </span>
              <span className="italic font-medium text-terracotta">Away.</span>
            </span>
          </h1>

          <p className="mt-8 max-w-xl text-base md:text-lg text-charcoal/80 leading-relaxed">
            We'll Keep It Business, While Being Personable. Delivered by Humans.
          </p>

          <div className="mt-10 max-w-xl flex justify-center">
            <Link
              to="/quote"
              data-testid={HERO.ctaQuote}
              className="group inline-flex items-center gap-2 rounded-full bg-terracotta hover:bg-terracotta-hover text-cream px-6 py-3.5 text-sm font-bold transition-colors"
            >
              Get a Quote
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-3 max-w-xl gap-6">
            {[
              ["24hr", "avg. quote turnaround"],
              ["100% Certified", "background-checked · medical-trained drivers"],
              ["Proof of Delivery", "SMS / email confirmation"],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="font-serif text-xl md:text-2xl text-forest leading-tight">{k}</div>
                <div className="mt-2 text-[11px] uppercase tracking-widest text-charcoal/60 leading-snug">
                  {v}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Hero photograph */}
        <div className="col-span-12 lg:col-span-5 relative min-h-[340px] lg:min-h-0">
          <div className="relative aspect-[3/4] lg:aspect-auto lg:h-[85%] lg:mt-12 overflow-hidden frame-hover">
            <img
              data-testid={HERO.image}
              src={HERO_IMG}
              alt="A curly-haired shop owner accepting a confidential manila envelope from a Waylio driver in a green t-shirt outside her retail shop"
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-forest/10" />
          </div>
        </div>
      </div>

      {/* On the road — busy city illustration band */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-16 md:mt-24">
        <div className="border-t border-forest/15 pt-10 md:pt-14">
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-8 md:mb-10">
            <div className="col-span-12 md:col-span-6">
              <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-3">
                On the road
              </div>
              <h3 className="font-serif text-2xl md:text-3xl text-forest leading-[1.15]">
                We'll Make a Way So That You Can Have Your{" "}
                <span className="italic font-light">Day</span>.
              </h3>
            </div>
            <div className="col-span-12 md:col-span-6">
              <p className="text-sm md:text-base text-charcoal/70 max-w-md md:ml-auto">
                Same driver, same time, the same care from your door to theirs.
                Waylio is a small-business courier — never a generic fleet.
              </p>
            </div>
          </div>
          <img
            data-testid="hero-route-illustration"
            src={ROUTE_ILLUSTRATION}
            alt="Illustration of a Waylio delivery van working its way through a busy city street"
            className="w-full h-auto"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
}
